import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSheetSelection } from '../../contexts/sheetSelection';
import { Text } from '../Typography';
import MessageComposer from '../MessageComposer/MessageComposer';
import { MessageList } from './MessageList';
import { useMessages } from '@/hooks/useMessages';
import { useChats } from '@/hooks/useChats';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Message } from '@/types/chat';
import { WorkflowCard } from '../WorkflowCard/WorkflowCard';
import { useMobile } from '@/hooks/useMobile';
import { useTagOverview } from '@/hooks/useTagOverview';
import { TagOverviewGraph } from '@/components/TagOverviewGraph';
import { FullscreenModal } from '@/components/Overlay';
import { motion } from 'motion/react';

const SCENARIO_WORKFLOW = 'scenario_analysis';
const STAKEHOLDER_WORKFLOW = 'stakeholder_directive';

const WORKFLOWS = [
  {
    title: 'Scenario Analysis',
    description: 'Analyze how external events impact your portfolio companies',
    prompt:
      'Run a scenario analysis on how current external events are impacting our portfolio companies.',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    title: 'Stakeholder Directive',
    description: 'Draft targeted communications to portfolio company CEOs',
    prompt:
      'Draft stakeholder communications for our portfolio company CEOs based on the latest scenario analysis.',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
    ),
  },
  {
    title: 'Board Decisions',
    description: 'Model board responses and design governance submissions',
    prompt:
      'Model board responses and design governance submissions for the upcoming board decisions.',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="2" x2="12" y2="6" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <line x1="8" y1="6" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="18" />
      </svg>
    ),
  },
];

export const Chat: FC = () => {
  const {
    selectedSheetId,
    setSelectedSheetId,
    selectedCategoryIds,
    selectedTagIds,
    setSelectedTagIds,
  } = useSheetSelection();
  const { chatId, accountId } = useParams<{ chatId?: string; accountId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const isCategoryMode = selectedCategoryIds.length > 0;
  const numericChatId = chatId && chatId !== 'new' ? Number(chatId) : null;
  const hasValidChatId = numericChatId != null && !Number.isNaN(numericChatId);
  const currentCategoryIdsParam = searchParams.get('categoryIds') ?? '';
  const selectedWorkflow = searchParams.get('workflow');
  const isScenarioWorkflow = selectedWorkflow === SCENARIO_WORKFLOW;
  const isStakeholderWorkflow = selectedWorkflow === STAKEHOLDER_WORKFLOW;
  const { messages, regenerate, sendStreaming, cancelStreaming } = useMessages(
    accountId,
    hasValidChatId ? numericChatId : null,
    { skipHistoryLoad: isCategoryMode && !hasValidChatId }
  );
  const { remove: removeChat } = useChats(accountId, { skipLoad: isCategoryMode });
  const canChat = isCategoryMode || selectedSheetId != null;
  const tagOverviewEnabled = !isCategoryMode && selectedSheetId != null;
  const { data: tagOverview, loading: tagsLoading, error: tagsError, refetch: refetchTagOverview } = useTagOverview(
    selectedSheetId,
    tagOverviewEnabled
  );
  const hasTagOverview = (tagOverview?.tags.length ?? 0) > 0;
  const shouldShowTagScopeButton = tagOverviewEnabled && hasTagOverview;
  const scopedTagIds = useMemo(
    () => (!isCategoryMode && selectedTagIds.length > 0 ? selectedTagIds : undefined),
    [isCategoryMode, selectedTagIds]
  );
  const pinnedTags = useMemo(() => {
    if (!tagOverview || selectedTagIds.length === 0) return [];

    const titlesById = new Map(tagOverview.tags.map((tag) => [tag.id, tag.title]));
    return selectedTagIds
      .map((tagId) => {
        const label = titlesById.get(tagId);
        return label ? { id: tagId, label } : null;
      })
      .filter((tag): tag is { id: number; label: string } => tag !== null);
  }, [selectedTagIds, tagOverview]);

  const buildSearch = (extras?: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(extras ?? {}).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    const next = params.toString();
    return next ? `?${next}` : '';
  };

  useEffect(() => {
    if (isCategoryMode && selectedTagIds.length > 0) {
      setSelectedTagIds([]);
    }
  }, [isCategoryMode, selectedTagIds.length, setSelectedTagIds]);

  useEffect(() => {
    if (!tagOverview) return;
    const validTagIds = new Set(tagOverview.tags.map(tag => tag.id));
    const nextSelectedTagIds = selectedTagIds.filter(tagId => validTagIds.has(tagId));
    if (nextSelectedTagIds.length !== selectedTagIds.length) {
      setSelectedTagIds(nextSelectedTagIds);
    }
  }, [tagOverview, selectedTagIds, setSelectedTagIds]);

  useEffect(() => {
    if (isCategoryMode && isTagModalOpen) {
      setIsTagModalOpen(false);
    }
  }, [isCategoryMode, isTagModalOpen]);

  const selectWorkflow = (workflow: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('workflow', workflow);
      return next;
    });
  };

  const handleSubmit = async (text: string, files?: File[]) => {
    if (!accountId) return;

    const workflow = selectedWorkflow || undefined;

    if (isCategoryMode) {
      let navigated = false;
      const ensureNavigation = (targetChatId: number | null | undefined) => {
        if ((chatId && chatId !== 'new') || navigated) return;
        if (targetChatId == null || Number.isNaN(targetChatId)) return;
        navigated = true;
        const categoryIdsValue = currentCategoryIdsParam || selectedCategoryIds.map((id) => String(id)).join(',');
        navigate(
          `/account/${accountId}/chat/${targetChatId}${buildSearch({
            categoryIds: categoryIdsValue,
            workflow,
          })}`
        );
      };

      const newChatId = await sendStreaming(text, null, {
        categoryIds: selectedCategoryIds,
        workflow,
        files,
        onChatResolved: (resolvedChatId) => {
          ensureNavigation(resolvedChatId);
        },
      });

      ensureNavigation(newChatId);
    } else {
      if (!selectedSheetId) return;

      let navigated = false;
      const ensureNavigation = (targetChatId: number | null | undefined) => {
        if ((chatId && chatId !== 'new') || navigated) return;
        if (targetChatId == null || Number.isNaN(targetChatId)) return;
        navigated = true;
        navigate(`/account/${accountId}/chat/${targetChatId}${buildSearch({ workflow })}`);
      };

      const newChatId = await sendStreaming(text, selectedSheetId, {
        selectedTagIds: scopedTagIds,
        workflow,
        files,
        onChatResolved: (resolvedChatId) => {
          ensureNavigation(resolvedChatId);
        },
      });

      ensureNavigation(newChatId);
    }
  };

  const handleRegenerate = async (message: Message) => {
    if (!accountId) return;
    if (!isCategoryMode && !selectedSheetId) return;

    const promptToSend = message.title;
    if (!promptToSend) return;

    if (isCategoryMode) {
      await regenerate(promptToSend, null, message.id, message.title || '', {
        categoryIds: selectedCategoryIds,
        workflow: selectedWorkflow || undefined,
      });
    } else {
      await regenerate(promptToSend, selectedSheetId!, message.id, message.title || '', {
        selectedTagIds: scopedTagIds,
        workflow: selectedWorkflow || undefined,
      });
    }
  };

  const handleDeleteChat = useCallback(async () => {
    if (!chatId || !accountId) return;
    try {
      const success = await removeChat(chatId);
      if (success) {
        setSelectedSheetId(null);
        navigate(`/account/${accountId}`);
      }
    } catch (error) {
      if (import.meta.env?.DEV) {
        console.error('Failed to delete chat', error);
      }
    }
  }, [chatId, accountId, navigate, removeChat, setSelectedSheetId]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!chatId) return;
      const isDeleteKey = event.key === 'Backspace' || event.key === 'Delete';
      const isModifierHeld = event.metaKey || event.ctrlKey;
      if (!isDeleteKey || !isModifierHeld || event.repeat) return;
      event.preventDefault();
      void handleDeleteChat();
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [chatId, handleDeleteChat]);

  const handleCancelStreaming = () => {
    cancelStreaming();
  };

  if (!accountId) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Text variant="text-lg-bold" className="text-grey-medium">
            Select an account to start
          </Text>
          <Text variant="text-md-regular" className="text-grey-medium">
            Use the account switcher in the sidebar to choose a workspace first.
          </Text>
        </div>
      </div>
    );
  }

  const isEmpty = !canChat || messages.length === 0;
  const showScenarioWelcome = isEmpty && canChat && isScenarioWorkflow;
  const showStakeholderWelcome = isEmpty && canChat && isStakeholderWorkflow;
  const showWorkflowWelcome = showScenarioWelcome || showStakeholderWelcome;

  const workflowHandlers: Record<number, string> = {
    0: SCENARIO_WORKFLOW,
    1: STAKEHOLDER_WORKFLOW,
  };

  if (isEmpty) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-[860px] flex-col items-center justify-start gap-3 px-3 py-3 md:justify-center md:gap-6 md:py-8">
            <img
              src="/PIF-logo.png"
              alt="PIF"
              className="hidden h-12 w-[108px] object-contain md:block md:h-[60px] md:w-[134px]"
            />

            <h2 className="max-w-[320px] text-center text-[18px] font-bold leading-tight tracking-[0.2px] text-grey-black md:max-w-none md:text-[24px]">
              {showScenarioWelcome
                ? 'Scenario Analysis Mode'
                : showStakeholderWelcome
                  ? 'Stakeholder Directive Mode'
                  : 'Welcome to the PIF Executives Chat'}
            </h2>

            {showScenarioWelcome && (
              <div className="w-full rounded-2xl border border-peach-1 bg-white p-3 shadow-subtle md:max-w-[560px] md:p-6">
                <p className="mb-3 text-md font-medium text-grey-black">Welcome to the PIF scenario analyser.</p>
                <ol className="list-decimal space-y-2 pl-5 text-md leading-[24px] text-grey-hover">
                  <li>Paste a news link/article or write a short scenario summary.</li>
                  <li>Provide start and end dates when this scenario affects the business.</li>
                  <li>Choose one case: Best Case, Base Case, or Worst Case.</li>
                </ol>
              </div>
            )}

            {showStakeholderWelcome && (
              <div className="w-full rounded-2xl border border-peach-1 bg-white p-3 shadow-subtle md:max-w-[560px] md:p-6">
                <p className="mb-3 text-md font-medium text-grey-black">Welcome to the PIF stakeholder directive tool.</p>
                <ol className="list-decimal space-y-2 pl-5 text-md leading-[24px] text-grey-hover">
                  <li>Specify the topic or context for the communication.</li>
                  <li>Select the target audience (e.g., portfolio company CEOs).</li>
                  <li>Define the tone and urgency level.</li>
                  <li>Attach additional information that needs to be taken into account to generate an email.</li>
                </ol>
              </div>
            )}

            {!showWorkflowWelcome && canChat && (
              <div className="flex w-full flex-col gap-2 md:hidden">
                {WORKFLOWS.map((wf, index) => {
                  const workflow = workflowHandlers[index];
                  const isDisabled = !workflow;

                  return (
                    <button
                      key={wf.title}
                      type="button"
                      onClick={() => {
                        if (workflow) {
                          selectWorkflow(workflow);
                        }
                      }}
                      disabled={isDisabled}
                      aria-disabled={isDisabled}
                      className={`w-full rounded-[22px] border border-peach-1 px-4 py-2.5 text-left transition-colors ${
                        isDisabled
                          ? 'cursor-default bg-white'
                          : 'bg-white hover:bg-peach/40'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg [&>svg]:h-5 [&>svg]:w-5 ${
                            isDisabled ? 'bg-peach text-pif-green' : 'bg-peach text-pif-green'
                          }`}
                        >
                          {wf.icon}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[15px] font-bold leading-tight text-grey-black">
                            {wf.title}
                          </p>
                          <p className="mt-0.5 line-clamp-2 text-[13px] leading-[1.25rem] text-grey-hover">
                            {wf.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {!showWorkflowWelcome && canChat && (
              <div className="hidden w-full max-w-[860px] md:flex md:flex-row md:justify-center md:gap-4">
                {WORKFLOWS.map((wf, index) => (
                  <WorkflowCard
                    key={wf.title}
                    icon={wf.icon}
                    title={wf.title}
                    description={wf.description}
                    disabled={!(index in workflowHandlers)}
                    onClick={() => {
                      const workflow = workflowHandlers[index];
                      if (workflow) selectWorkflow(workflow);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:pb-0">
          <MessageComposer
            placeholder="Ask something"
            onSubmit={handleSubmit}
            autoFocus={false}
            onOpenTagScope={
              shouldShowTagScopeButton
                ? () => {
                    setIsTagModalOpen(true);
                  }
                : undefined
            }
            pinnedTags={pinnedTags}
            onRemovePinnedTag={(tagId) => setSelectedTagIds(selectedTagIds.filter((id) => id !== tagId))}
          />
        </div>

        {tagOverview && (
          <FullscreenModal open={isTagModalOpen} onClose={() => setIsTagModalOpen(false)}>
            <motion.div
              className="flex h-full w-full flex-col"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.04, delayChildren: 0.06 } },
              }}
            >
              <motion.div
                className="flex items-center justify-between border-b border-peach-1 px-5 py-4 md:px-7"
                variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
              >
                <div>
                  <Text variant="text-lg-bold" className="text-grey-black">
                    Tag focus
                  </Text>
                  <Text variant="text-sm-regular" className="text-grey-hover">
                    Select tags to focus the answer around this part of the model.
                  </Text>
                </div>

                <div className="flex items-center gap-3">
                  {selectedTagIds.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setSelectedTagIds([])}
                      className="rounded-full border border-peach-1 bg-white px-3.5 py-2 text-sm font-medium text-pif-green hover:bg-peach"
                    >
                      Clear tags
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setIsTagModalOpen(false)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-peach-1 bg-white text-grey-black hover:bg-peach"
                    aria-label="Close tags view"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </motion.div>

              <motion.div
                className="min-h-0 flex-1 p-3 md:p-5"
                variants={{ hidden: { opacity: 0, scale: 0.985 }, visible: { opacity: 1, scale: 1 } }}
              >
                <div className="h-full rounded-[24px] border border-peach-1 bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(250,246,238,0.92)_100%)] p-2 md:p-4">
                  <TagOverviewGraph
                    overview={tagOverview}
                    selectedTagIds={selectedTagIds}
                    onSelectionChange={setSelectedTagIds}
                    loading={tagsLoading}
                    error={tagsError?.message ?? null}
                    onRetry={() => {
                      void refetchTagOverview();
                    }}
                    fullscreen
                  />
                </div>
              </motion.div>
            </motion.div>
          </FullscreenModal>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-auto">
        <MessageList
          messages={messages}
          onRegenerate={handleRegenerate}
          onCancel={handleCancelStreaming}
          onSubmitPrompt={handleSubmit}
        />
      </div>
      <div className="shrink-0 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:pb-0">
        <MessageComposer
          placeholder="Ask something"
          onSubmit={handleSubmit}
          autoFocus={!isMobile}
          onOpenTagScope={
            shouldShowTagScopeButton
              ? () => {
                  setIsTagModalOpen(true);
                }
              : undefined
          }
          pinnedTags={pinnedTags}
          onRemovePinnedTag={(tagId) => setSelectedTagIds(selectedTagIds.filter((id) => id !== tagId))}
        />
      </div>

      {tagOverview && (
        <FullscreenModal open={isTagModalOpen} onClose={() => setIsTagModalOpen(false)}>
          <motion.div
            className="flex h-full w-full flex-col"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.04, delayChildren: 0.06 } },
            }}
          >
            <motion.div
              className="flex items-center justify-between border-b border-peach-1 px-5 py-4 md:px-7"
              variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
            >
              <div>
                <Text variant="text-lg-bold" className="text-grey-black">
                  Tag focus
                </Text>
                <Text variant="text-sm-regular" className="text-grey-hover">
                  Select tags to focus the answer around this part of the model.
                </Text>
              </div>

              <div className="flex items-center gap-3">
                {selectedTagIds.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setSelectedTagIds([])}
                    className="rounded-full border border-peach-1 bg-white px-3.5 py-2 text-sm font-medium text-pif-green hover:bg-peach"
                  >
                    Clear tags
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setIsTagModalOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-peach-1 bg-white text-grey-black hover:bg-peach"
                  aria-label="Close tags view"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </motion.div>

            <motion.div
              className="min-h-0 flex-1 p-3 md:p-5"
              variants={{ hidden: { opacity: 0, scale: 0.985 }, visible: { opacity: 1, scale: 1 } }}
            >
              <div className="h-full rounded-[24px] border border-peach-1 bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(250,246,238,0.92)_100%)] p-2 md:p-4">
                <TagOverviewGraph
                  overview={tagOverview}
                  selectedTagIds={selectedTagIds}
                  onSelectionChange={setSelectedTagIds}
                  loading={tagsLoading}
                  error={tagsError?.message ?? null}
                  onRetry={() => {
                    void refetchTagOverview();
                  }}
                  fullscreen
                />
              </div>
            </motion.div>
          </motion.div>
        </FullscreenModal>
      )}
    </div>
  );
};

export default Chat;
